/**
 * SelfConsumptionChart - Recharts BarChart showing 12-month self-consumption breakdown.
 *
 * Stacked bars: self-consumed, exported, grid-import.
 */

import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Text, Paper } from '@mantine/core';
import type { ProsumerSelfConsumption } from '../../types';

export interface SelfConsumptionChartProps {
  selfConsumption: ProsumerSelfConsumption;
}

const MONTH_LABELS = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

export function SelfConsumptionChart({ selfConsumption }: SelfConsumptionChartProps) {
  if (!selfConsumption) return null;
  const { monthlyRatios, totalProductionKwh, totalGridImportKwh } = selfConsumption;

  // Create chart data - approximate monthly values from ratios
  // This is a visualization approximation; real values would come from backend
  const monthlyProduction = totalProductionKwh / 12;

  const data = MONTH_LABELS.map((month, idx) => {
    const ratio = monthlyRatios[idx] || 0;
    const selfConsumed = monthlyProduction * ratio;
    const exported = monthlyProduction * (1 - ratio);
    const gridImport = (totalGridImportKwh / 12);

    return {
      month,
      selfConsumed: Math.round(selfConsumed),
      exported: Math.round(exported),
      gridImport: Math.round(gridImport),
    };
  });

  return (
    <Paper withBorder p="md" radius="md">
      <Text fw={600} mb="sm">Bilans energetyczny (miesięczny)</Text>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis label={{ value: 'kWh', angle: -90, position: 'insideLeft' }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="selfConsumed" name="Autokonsumpcja" stackId="a" fill="#40c057" />
          <Bar dataKey="exported" name="Oddane do sieci" stackId="a" fill="#fab005" />
          <Bar dataKey="gridImport" name="Pobrane z sieci" fill="#228be6" />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
